import React, { createContext, useState, useEffect, useLayoutEffect, useRef, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { hasPersistedTheme, loadPersistedTheme, savePersistedTheme, userThemeStorageKey } from '../Utilities/themeUserStorage';
import { UserContext } from './UserContext';

const ThemeContext = createContext();

const LIGHT_THEME_MARKERS = ['light.css', 'styles/light', 'styles_light'];
const DARK_THEME_MARKERS = ['mdb.dark.min.css', 'dark.css'];
const HIGH_CONTRAST_MARKERS = ['highcontrast.css'];

const THEME_NODES_SELECTOR =
  "head link[rel='stylesheet'], head style[data-href], head style[data-webpack]";

/** Login / forgot-password always use light shell; user preference still stored in context. */
const AUTH_LIGHT_ONLY_PATHS = new Set(["/", "/login", "/forgot-password"]);

/** CRA `PUBLIC_URL` subpath — compare route tail so `/app/forgot-password` matches forgot-password. */
function pathnameWithoutPublicUrl(pathname) {
  let base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  if (!base) return pathname;
  if (!base.startsWith("/")) base = `/${base}`;
  if (pathname === base) return "/";
  if (pathname.startsWith(`${base}/`)) return pathname.slice(base.length) || "/";
  return pathname;
}

/** Normalize trailing slashes so `/forgot-password/` matches auth set. */
function routePathForThemeMatching(pathname) {
  const stripped = pathnameWithoutPublicUrl(pathname);
  if (stripped.length > 1 && stripped.endsWith("/")) {
    return stripped.replace(/\/+$/, "");
  }
  return stripped;
}

function getNodeSignature(node) {
  const href =
    (node?.getAttribute && (node.getAttribute('href') || node.getAttribute('data-href'))) || '';
  const text = node?.textContent || '';
  return `${href} ${text}`.toLowerCase();
}

function setNodeEnabled(node, enabled) {
  if (!node) return;
  const tag = (node.tagName || '').toUpperCase();
  try {
    if (tag === 'LINK') {
      node.disabled = !enabled;
      return;
    }
    if (tag === 'STYLE') {
      if (node.sheet && 'disabled' in node.sheet) {
        node.sheet.disabled = !enabled;
      }
      node.media = enabled ? 'all' : 'not all';
    }
  } catch (_) {
    /* ignore */
  }
}

/** @returns {'light'|'dark'|'high-contrast'|null} */
function classifyThemeNode(node) {
  const signature = getNodeSignature(node);
  if (LIGHT_THEME_MARKERS.some((m) => signature.includes(m))) return 'light';
  if (DARK_THEME_MARKERS.some((m) => signature.includes(m))) return 'dark';
  if (HIGH_CONTRAST_MARKERS.some((m) => signature.includes(m))) return 'high-contrast';
  return null;
}

const ThemeProvider = ({ children }) => {
  const { user } = useContext(UserContext);
  const [theme, setTheme] = useState('light');
  const [osPrefersDark, setOsPrefersDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const { pathname } = useLocation();
  /** Keep theme css nodes and toggle enabled/disabled to avoid chunk-cache reimport issues. */
  const themeStyleNodesRef = useRef({
    light: [],
    dark: [],
    'high-contrast': [],
  });
  const themeOwnerKey = userThemeStorageKey(user);

  const forceLightShell = AUTH_LIGHT_ONLY_PATHS.has(routePathForThemeMatching(pathname));
  const appliedTheme = forceLightShell ? 'light' : theme;

  const isDarkUi =
    !forceLightShell &&
    (appliedTheme === 'dark' ||
      appliedTheme === 'high-contrast' ||
      (appliedTheme === 'OS-theme' && osPrefersDark));

  useLayoutEffect(() => {
    // Hydrate from SecureStorage / localStorage (login + theme use a full page reload — see Login / useLogout).
    setTheme('light');
    if (!themeOwnerKey) {
      return;
    }
    if (!hasPersistedTheme(user)) {
      setTheme('light');
      return;
    }
    const stored = loadPersistedTheme(user);
    setTheme(stored || 'light');
  }, [themeOwnerKey, user]);

  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
    if (!themeOwnerKey) return;
    try {
      savePersistedTheme(user, newTheme);
    } catch (e) {
      console.error('Theme preference save failed', e);
    }
  };

  // Body theme classes + OS preference (single listener; drives custom.css dark UI overrides)
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncBodyThemeClass = () => {
      const osDark = media.matches;
      setOsPrefersDark(osDark);
      document.documentElement.classList.toggle('theme-auth-light', forceLightShell);
      document.body.classList.toggle('theme-auth-light', forceLightShell);
      const useDarkUi =
        !forceLightShell &&
        (appliedTheme === 'dark' ||
          appliedTheme === 'high-contrast' ||
          (appliedTheme === 'OS-theme' && osDark));
      document.body.classList.toggle('theme-dark', useDarkUi);
      document.body.classList.toggle('theme-high-contrast', appliedTheme === 'high-contrast');
    };
    syncBodyThemeClass();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', syncBodyThemeClass);
      return () => media.removeEventListener('change', syncBodyThemeClass);
    }
    if (typeof media.addListener === 'function') {
      media.addListener(syncBodyThemeClass);
      return () => media.removeListener(syncBodyThemeClass);
    }
    return undefined;
  }, [appliedTheme, forceLightShell]);

  // Mobile browsers / PWA use meta theme-color for status bar and chrome; static #000 in index.html looked wrong in light mode.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    if (forceLightShell) {
      meta.setAttribute('content', '#f2f2f2');
    } else if (isDarkUi) {
      meta.setAttribute('content', '#212121');
    } else {
      meta.setAttribute('content', '#ffffff');
    }
  }, [isDarkUi, forceLightShell]);

  // Helps Safari/iOS form controls and overscroll match the in-app theme.
  useEffect(() => {
    if (forceLightShell) {
      document.documentElement.style.setProperty('color-scheme', 'light');
    } else {
      document.documentElement.style.setProperty('color-scheme', isDarkUi ? 'dark' : 'light');
    }
  }, [isDarkUi, forceLightShell]);

  /**
   * Load theme CSS for the current route. Login / logout use full document reload so prior theme
   * link tags are not left in the DOM; this path handles SPA navigation within the portal.
   */
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const setNodesEnabled = (nodes, enabled) => {
          nodes.forEach((node) => {
            setNodeEnabled(node, enabled);
          });
        };

        const disableAllThemes = () => {
          const domNodes = Array.from(document.querySelectorAll(THEME_NODES_SELECTOR));
          domNodes.forEach((node) => {
            const type = classifyThemeNode(node);
            if (!type) return;
            if (!themeStyleNodesRef.current[type].includes(node)) {
              themeStyleNodesRef.current[type].push(node);
            }
            setNodeEnabled(node, false);
          });
          Object.values(themeStyleNodesRef.current).forEach((nodes) => {
            setNodesEnabled(nodes, false);
          });
        };

        const captureNewNodes = (beforeSet) =>
          Array.from(document.querySelectorAll(THEME_NODES_SELECTOR)).filter((node) => !beforeSet.has(node));

        const ensureThemeNodesLoaded = async (targetTheme) => {
          if (themeStyleNodesRef.current[targetTheme]?.length) {
            return;
          }
          const before = new Set(Array.from(document.querySelectorAll(THEME_NODES_SELECTOR)));
          if (targetTheme === 'dark') {
            await import('mdb-react-ui-kit/dist/css/mdb.dark.min.css');
            await import('./../styles/dark.css');
          } else if (targetTheme === 'high-contrast') {
            await import('mdb-react-ui-kit/dist/css/mdb.dark.min.css');
            await import('./../styles/highcontrast.css');
          } else {
            await import('./../styles/light.css');
          }
          if (cancelled) return;
          themeStyleNodesRef.current[targetTheme] = captureNewNodes(before);
        };

        disableAllThemes();

        if (cancelled) return;

        if (forceLightShell) {
          /* SPA navigation from portal disables dark CSS via disableAllThemes() — we must load + enable
           * light.css again or outlined inputs / cards fall back to stale variables (forgot-password looks broken). */
          await ensureThemeNodesLoaded("light");
          if (cancelled) return;
          setNodesEnabled(themeStyleNodesRef.current.light || [], true);
          return;
        }
        const osDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const effectiveTheme = theme === 'OS-theme' ? (osDark ? 'dark' : 'light') : theme;
        if (!['light', 'dark', 'high-contrast'].includes(effectiveTheme)) {
          return;
        }
        await ensureThemeNodesLoaded(effectiveTheme);
        if (cancelled) return;
        setNodesEnabled(themeStyleNodesRef.current[effectiveTheme] || [], true);
      } catch (e) {
        console.error('Theme CSS load failed', e);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [forceLightShell, theme, osPrefersDark, themeOwnerKey]);

  // Value to be provided to consuming components
  const contextValue = {
    theme,
    /** Theme actually applied to document/CSS (light on login / forgot-password). */
    appliedTheme,
    /** True when dark/high-contrast is active, or OS-theme with dark system preference (portal only). */
    isDarkUi,
    setTheme: toggleTheme,
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

export { ThemeContext, ThemeProvider };
